import * as React from 'react';
import styled, { css, DefaultTheme, useTheme } from 'styled-components';
import { isFunction, memoizeFunction, renderClasses } from '@agilepoint/utils-common';
import { Icon } from '../../icon';
import { Typography } from '../../typography';
import { ITreeItemProps } from '../tree-view-types';
import { StackPanel } from '../../stack-panel';
import { TreeViewState } from '../theme';
import { extractNumberAsString, useChooseRef, useKeyStrokeListener } from '../../../utils';
import { Spinner } from '../../spinner';

const StyledMenuIcon = styled(Icon).attrs({
    iconName: 'ap-right-arrow-regular.outline'
}) <{ $isExpanded: boolean; }>`
    transform-origin: center;
    transform: rotate(${({ $isExpanded }) => ($isExpanded ? '90deg' : '0deg')});
    transition: all 0.15s ease-in-out;
`;

const labelStyle = css`
    max-width: 100px;
    text-overflow: ellipsis;
    overflow: hidden;
`;

const RegularLabel = styled(Typography.Medium).attrs({ className: String('ap-treeView-itemLabel') })`
    ${labelStyle}
`;

const Header = styled(Typography.H5).attrs({ className: String('ap-treeView-itemLabel') })`
    ${labelStyle}
`;

const getColorVariablesByItemState = ({ treeView: { colors: { item } } }: DefaultTheme, itemState: TreeViewState) => item[itemState];

const getStylesByItemState = memoizeFunction((itemState: TreeViewState) => css`
        background-color: ${({ theme }) => getColorVariablesByItemState(theme, itemState).backgroundColor};
        border-color: ${({ theme }) => getColorVariablesByItemState(theme, itemState).borderColor};

        ${Icon} {
            fill: ${({ theme }) => getColorVariablesByItemState(theme, itemState).mainIcon};
        }

        ${StyledMenuIcon} {
            fill: ${({ theme }) => getColorVariablesByItemState(theme, itemState).menuIcon};
        }

        ${RegularLabel}, ${Header} {
            color: ${({ theme }) => getColorVariablesByItemState(theme, itemState).text};
        }
    `);

interface IItemWrapperProps {
    $isSelected?: boolean;
    $isSelectable?: boolean;
    $shouldHighlightItem?: boolean;
}

const ItemWrapper = styled.div.attrs(({ $isSelected, $shouldHighlightItem }: IItemWrapperProps) => ({
    className: renderClasses({
        'selected': $isSelected,
        'descendant-selected': ($shouldHighlightItem && !$isSelected)
    }, 'ap-treeView-itemWrapper')
})) <IItemWrapperProps>`
    border-radius: ${({ theme: { treeView: { composites } } }) => composites.borderRadius.item};
    border-width: ${({ theme: { treeView: { composites } } }) => composites.borderWidth.item};
    border-style: solid;
    display: flex;
    justify-content: space-between;
    margin: ${({ theme: { semantics: { gap } } }) => `${gap.l} ${gap.none}`};
    padding: ${({ theme: { semantics: { gap } } }) => `${gap.l} ${gap.xl}`};
    transition: all 0.15s ease-in-out;

    ${({ $shouldHighlightItem, $isSelected }) => ($isSelected ? getStylesByItemState('selected') : ($shouldHighlightItem ? getStylesByItemState('expanded') : getStylesByItemState('default')))}

    :hover {
        ${({ $isSelectable, $shouldHighlightItem, $isSelected }) => ($isSelectable && !$shouldHighlightItem && !$isSelected && getStylesByItemState('hovered'))}
    }

    :focus {
        outline: inherit;
        box-shadow: 0px 0px 4px ${({ theme: { semantics: { palette } } }) => palette.themePrimary};
    }
`;

const ChildItemsWrapper = styled.div.attrs(() => ({
    className: String('ap-treeView-childItemsWrapper')
}))`
    padding-left: ${({ theme: { semantics: { gap } } }) => gap.xl};
`;

export const TreeItem = React.memo((props: ITreeItemProps) => {
    const {
        className = '', elementRef, isExpanded, item, hasChildren: hasChildrenFromProps, onDefaultExpanded,
        triggerChangeOnParentSelection, loadOnDemand, shouldHighlightItem, isInCompactMode, onRenderContent,
        defaultExpanded, getChildItems, onRenderItem, onClick, renderChildren, onExpandToggle
    } = props;
    const { id, iconName, iconSize, label, hasChildren: hasChildrenFromItemInfo } = item;

    const rootElementRef = useChooseRef(elementRef);
    const { subscribe, unsubscribe } = useKeyStrokeListener(rootElementRef);

    const { semantics: { gap: { xl } } } = useTheme();
    const gap = React.useMemo(() => extractNumberAsString(xl), [xl]);

    const [shouldFetchChildren, setFetchState] = React.useState(hasChildrenFromItemInfo !== false && !!loadOnDemand);
    const [isLoading, setLoadingState] = React.useState(false);

    const shouldRenderAllContent = !isInCompactMode;
    const hasChildren = React.useMemo((): boolean => {
        if (!shouldRenderAllContent) {
            return false;
        }
        if (hasChildrenFromItemInfo !== undefined) {
            return hasChildrenFromItemInfo;
        }
        return (hasChildrenFromProps || shouldFetchChildren);
    }, [hasChildrenFromItemInfo, hasChildrenFromProps, shouldFetchChildren, shouldRenderAllContent]);

    const childItems = React.useMemo(() => getChildItems(id), [id, getChildItems]);
    const Label = React.useMemo(() => (hasChildren ? Header : RegularLabel), [hasChildren]);

    const expandItem = React.useCallback(async () => {
        if (shouldFetchChildren) {
            setLoadingState(true);
        }
        if (hasChildren || shouldFetchChildren) {
            await onExpandToggle(id, shouldFetchChildren);
            setFetchState(false);
            setLoadingState(false);
        }
    }, [hasChildren, id, onExpandToggle, shouldFetchChildren]);

    const _onClick = React.useCallback(async () => {
        expandItem();
        if (hasChildren && !triggerChangeOnParentSelection) {
            return;
        }
        onClick(id);
    }, [expandItem, hasChildren, triggerChangeOnParentSelection, onClick, id]);

    React.useMemo(() => {
        if (!(defaultExpanded && hasChildren)) {
            return;
        }
        onDefaultExpanded(id);
        if (isExpanded) {
            return;
        }
        expandItem();
    }, [defaultExpanded, hasChildren, onDefaultExpanded, id, isExpanded, expandItem]);

    React.useEffect(() => {
        subscribe(_onClick, ' ', 'Enter');
        return unsubscribe;
    }, [subscribe, unsubscribe, _onClick]);

    React.useEffect(() => {
        const { current } = rootElementRef;
        if (!current || !shouldHighlightItem || hasChildren) {
            return;
        }
        current.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
    }, [hasChildren, rootElementRef, shouldHighlightItem]);

    const _renderContent = () => (
        isFunction(onRenderContent) ? onRenderContent({ isInCompactMode, item, isLoading })
            : (
                <React.Fragment>
                    <Icon iconName={iconName!} iconSize={iconSize} hidden={!iconName} />
                    <Label hidden={!(shouldRenderAllContent && label)}>{label}</Label>
                    <Spinner hidden={!isLoading} size="small" spinnerType="indeterminate" />
                </React.Fragment>
            )
    )

    // The below method is not memoized using useCallback as the dependencies change too often.
    const _renderDefault = () => (
        <ItemWrapper
            key={id}
            $shouldHighlightItem={shouldHighlightItem}
            $isSelected={shouldHighlightItem && !hasChildren}
            $isSelectable={triggerChangeOnParentSelection || !hasChildren}
            onClick={_onClick}
            tabIndex={isInCompactMode ? -1 : 0}>
            <StackPanel title={label} gap={gap} alignment="center">
                <StyledMenuIcon $isExpanded={isExpanded}
                    iconSize="12" hidden={!hasChildren} />
                {_renderContent()}
            </StackPanel>
        </ItemWrapper>
    );

    return (
        <div className={renderClasses(['ap-treeView-item', className])} ref={rootElementRef}>
            {onRenderItem
                ? onRenderItem({
                    hasChildren, item, isInCompactMode, shouldHighlightItem, isExpanded, onClick: _onClick,
                    renderDefault: _renderDefault, isLoading, loadOnDemand
                })
                : _renderDefault()}
            {
                (shouldRenderAllContent && hasChildren && isExpanded) && (
                    <ChildItemsWrapper>
                        {childItems.map(renderChildren)}
                    </ChildItemsWrapper>
                )
            }
        </div>
    );
});

TreeItem.displayName = 'TreeItem';
